// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- ambient module declaration; must travel with this file when consumers type-check it from source
/// <reference path="../redux-persist.d.ts" />
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { userApi } from '../services/UserService.js';
import { registrationApi } from '../services/RegistrationService.js';
import { notesApi } from '../services/NotesService.js';
import { loginApi, securityApi } from '../services/SecurityService.js';
import { adminApi } from '../services/AdminService.js';
import { commsApi } from '../services/CommsService.js';
import { announcementsApi } from '../services/AnnouncementsService.js';
import { feedbackApi } from '../services/FeedbackService.js';
import { kbArticlesApi } from '../services/KbArticlesService.js';
import { bibleApi } from '../services/BibleService.js';
import { db } from '../model/db.js';
import { resetUIState, uiSlice } from './UISlice.js';
import { persistedUISlice, resetPersistedUIState } from './PersistedUI.js';
import { persistMigrate, persistVersion } from './PersistMigrations.js';
import { FLUSH, PAUSE, PERSIST, persistReducer, PURGE, REGISTER, REHYDRATE } from 'redux-persist';
import { setupListeners } from '@reduxjs/toolkit/query';
import storage from 'redux-persist/lib/storage/index.js';
import { commentSyncListenerMiddleware } from '../sync/CommentSyncMiddleware.js';
import { globalSyncListenerMiddleware } from '../sync/GlobalSyncMiddleware.js';
import { registerSyncStore } from '../sync/syncFetch.js';
import '../sync/registerSyncSources.js';
import '../sync/UnloadFlush.js';

const buildReducers = () =>
  combineReducers({
    [userApi.reducerPath]: userApi.reducer,
    [registrationApi.reducerPath]: registrationApi.reducer,
    [notesApi.reducerPath]: notesApi.reducer,
    [securityApi.reducerPath]: securityApi.reducer,
    [loginApi.reducerPath]: loginApi.reducer,
    [adminApi.reducerPath]: adminApi.reducer,
    [commsApi.reducerPath]: commsApi.reducer,
    [announcementsApi.reducerPath]: announcementsApi.reducer,
    [feedbackApi.reducerPath]: feedbackApi.reducer,
    [kbArticlesApi.reducerPath]: kbArticlesApi.reducer,
    [bibleApi.reducerPath]: bibleApi.reducer,
    ui: uiSlice.reducer,
    persistedUI: persistedUISlice.reducer,
  });

const apiMiddlewares = [
  userApi.middleware,
  registrationApi.middleware,
  notesApi.middleware,
  securityApi.middleware,
  loginApi.middleware,
  adminApi.middleware,
  commsApi.middleware,
  announcementsApi.middleware,
  feedbackApi.middleware,
  kbArticlesApi.middleware,
  bibleApi.middleware,
] as const;

const persistConfig = {
  key: 'root',
  version: persistVersion,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  storage: (storage as any).default || storage,
  whitelist: ['persistedUI'],
  migrate: persistMigrate,
};

const persistedReducer = persistReducer(persistConfig, buildReducers());

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: { ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER] },
    })
      .prepend(commentSyncListenerMiddleware.middleware)
      .prepend(globalSyncListenerMiddleware.middleware)
      .concat(...apiMiddlewares),
});

export type RootState = ReturnType<typeof store.getState>;

registerSyncStore(store);

setupListeners(store.dispatch);

/**
 * Builds a brand new store instance with the same reducers and
 * middleware as the app-wide singleton `store`, but without
 * `redux-persist` wired in (so instances don't clobber each other's
 * state via the shared `localStorage` persistence key). Intended for
 * Ladle stories and other places that need an isolated store per
 * instance rather than the shared singleton.
 */
export const createStore = () =>
  configureStore({
    reducer: buildReducers(),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware()
        .prepend(commentSyncListenerMiddleware.middleware)
        .prepend(globalSyncListenerMiddleware.middleware)
        .concat(...apiMiddlewares),
  });

export const clearAllCachedData = async () => {
  store.dispatch(resetUIState());
  store.dispatch(resetPersistedUIState());
  store.dispatch(userApi.util.resetApiState());
  store.dispatch(registrationApi.util.resetApiState());
  store.dispatch(notesApi.util.resetApiState());
  store.dispatch(securityApi.util.resetApiState());
  store.dispatch(loginApi.util.resetApiState());
  store.dispatch(adminApi.util.resetApiState());
  store.dispatch(commsApi.util.resetApiState());
  store.dispatch(announcementsApi.util.resetApiState());
  store.dispatch(feedbackApi.util.resetApiState());
  store.dispatch(kbArticlesApi.util.resetApiState());
  // purposely not clearing Bible API state; we can leave this cached
  // store.dispatch(bibleApi.util.resetApiState());

  // dexie is origin-scoped, not per-user - without this, a different user
  // logging into the same browser would still have local read access to
  // whatever this user's synced comments/settings left behind. Best-effort
  // and `allSettled` (not `all`): a failure here (e.g. IndexedDB
  // unavailable/blocked) shouldn't stop the rest of logout from completing,
  // and `all` would leave the *other* clears' rejections unhandled once it
  // settled on the first one.
  const results = await Promise.allSettled([
    db.comments.clear(),
    db.settings.clear(),
    db.syncMeta.clear(),
    db.passages.clear(),
    db.passageHashtags.clear(),
    db.actionTypes.clear(),
    db.actionEntries.clear(),
    db.graphs.clear(),
    db.serverMessages.clear(),
    db.prayers.clear(),
    db.prayerListItems.clear(),
    db.prayerHashtags.clear(),
    db.plans.clear(),
    db.instantiatedPlans.clear(),
    db.instantiatedPlanDays.clear(),
  ]);
  for (const result of results) {
    if (result.status === 'rejected') {
      // eslint-disable-next-line no-console
      console.error('clearAllCachedData: failed to clear local dexie storage', result.reason);
    }
  }
};
